import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { Store } from '@ngrx/store';
import { filter, take } from 'rxjs/operators';

import { Prefecture } from 'src/app/models/common-data';
import { enter } from 'src/app/resources/animations';
import { Log } from 'src/app/utils/log';
import { RootState } from 'src/app/reducers';
import { dispatchInfoMessage } from 'src/app/actions/core.actions';
import { PREFECTURES } from '../../../resources/config';
import { Daigaku, UpdateDaigakuData } from '../../../models/daigaku';
import { initializeUpdateDaigakuState, updateDaigaku } from 'src/app/actions/daigaku.actions';
import { getUpdateDaigakuResult } from 'src/app/selectors/daigaku.selectors';

export interface EditDaigakuDialogData {
  daigaku: Daigaku;
}

@Component({
  selector: 'app-edit-daigaku-dialog',
  templateUrl: './edit-daigaku-dialog.component.html',
  styleUrls: ['./edit-daigaku-dialog.component.scss'],
  animations: [enter]
})
export class EditDaigakuDialogComponent implements OnInit, OnDestroy {
  daigakuForm: UntypedFormGroup;
  prefectures: Prefecture[];
  daigakuId: string;
  daigakuEditing = false;
  errorMessage = '';
  dialogTitle: string;

  private LOG_SOURCE = this.constructor.name;

  constructor(
    private store: Store<RootState>,
    private dialogRef: MatDialogRef<EditDaigakuDialogComponent>,
    @Inject(MAT_DIALOG_DATA) private data: EditDaigakuDialogData
  ) {}

  ngOnInit() {
    this.prefectures = PREFECTURES;
    this.dialogTitle = '大学の編集';

    this.setUpForms();
    this.setUpValues();
  }

  ngOnDestroy() {
    this.store.dispatch(initializeUpdateDaigakuState());
  }

  editDaigaku() {
    if (this.daigakuForm.invalid) {
      this.daigakuForm.markAllAsTouched();
      return;
    }
    Log.debug(this.LOG_SOURCE, '大学を変更します');
    this.disableForms();
    this.errorMessage = '';

    const daigakuName: string = this.daigakuForm.get('daigakuName').value;
    const daigakuSubName: string = this.daigakuForm.get('daigakuSubName').value;
    const daigakuCode: string = this.daigakuForm.get('daigakuCode').value;
    const prefectureId: string = this.daigakuForm.get('prefectureId').value;

    const daigaku: UpdateDaigakuData = {
      id: this.data.daigaku.id,
      daigakuName,
      daigakuSubName,
      daigakuCode,
      prefectureId
    };
    Log.debug(this.LOG_SOURCE, 'Update Daigaku', daigaku);
    this.store.dispatch(updateDaigaku({ daigaku }));

    this.store
      .select(getUpdateDaigakuResult)
      .pipe(
        filter(it => it != null),
        take(1)
      )
      .subscribe(result => {
        this.store.dispatch(initializeUpdateDaigakuState());
        Log.debug(this.LOG_SOURCE, `edit daigaku result: `, result);
        if (result.success) {
          this.dialogRef.close(true);
          Log.debug(this.LOG_SOURCE, 'result.updatedDaigaku', result.updatedDaigaku);
          const message = `${result.updatedDaigaku.daigakuName} の変更が完了しました`;
          this.store.dispatch(
            dispatchInfoMessage({
              message
            })
          );
          Log.debug(this.LOG_SOURCE, `大学変更完了: `, result.updatedDaigaku);
          return;
        }

        Log.warn(this.LOG_SOURCE, `update user error: err.code: ${result.error ? result.error.code : 'none'}`, result.error);
        this.enableForms();
        // TODO: メッセージ内容を要検討
        this.errorMessage = result.error ? `[${result.error.code}] ${result.error.message}` : 'エラーが発生しました';
      });
  }

  cancel() {
    this.dialogRef.close(false);
  }

  // components internal methods ---------------------------------------------------------------
  onChangeOrganization() {
    this.daigakuId = null;
    this.daigakuForm.patchValue({ daigaku: null });
  }

  // set ups or private method ---------------------------------------------------------------

  private setUpForms() {
    this.daigakuForm = new UntypedFormGroup({
      daigakuName: new UntypedFormControl('', [Validators.required]),
      daigakuSubName: new UntypedFormControl('', []),
      daigakuCode: new UntypedFormControl('', [
        Validators.required,
        Validators.minLength(6),
        Validators.maxLength(6),
        Validators.pattern(/^-?(0|[1-9]\d*)?$/)
      ]),
      prefectureId: new UntypedFormControl('', [Validators.required])
    });
    this.daigakuForm.get('daigakuCode').disable();
  }

  private setUpValues() {
    this.daigakuForm.patchValue({
      daigakuName: this.data.daigaku.daigakuName,
      daigakuSubName: this.data.daigaku.daigakuSubName,
      daigakuCode: this.data.daigaku.daigakuCode.slice(2),
      prefectureId: this.data.daigaku.prefectureId
    });
  }

  private disableForms() {
    Object.keys(this.daigakuForm.controls).forEach(ctrlName => {
      this.daigakuForm.get(ctrlName).disable();
    });
    this.daigakuEditing = true;
  }

  private enableForms() {
    Object.keys(this.daigakuForm.controls).forEach(ctrlName => {
      this.daigakuForm.get(ctrlName).enable();
    });
    this.daigakuEditing = false;
    this.daigakuForm.get('daigakuCode').disable();
  }
}
